/**
 * dicomCache — one Cache Storage cache for fetched DICOM, shared by every
 * screen that pulls instances over HTTP.
 *
 * Earlier builds wrote under two other names; both are deleted once per page
 * load.
 *
 * The cache is kept across browser sessions on purpose: teaching cases are
 * de-identified and faculty and students reopen the same cases on their own
 * laptops. A byte budget keeps it bounded; Cache.keys() returns entries in
 * insertion order, so the oldest go first.
 */

const CACHE_NAME = 'helical-dicom';

// Caches written by earlier builds, removed once per page load so their space
// comes back without the user clearing site data by hand.
const LEGACY_CACHE_NAMES = ['dicom-files', 'helical-cloud-dicom'];

const LEGACY_SESSION_KEY = 'dicom-cache-session-id';

/** Cap on what this origin may hold. Override with window.__helicalDicomCacheMaxBytes. */
const DEFAULT_MAX_BYTES = 4 * 1024 * 1024 * 1024;

// Evicting is far more expensive than a put, so usage is only checked every
// so many puts, and eviction deletes in blocks rather than one at a time.
const PUTS_BETWEEN_CHECKS = 50;
const EVICT_BLOCK = 100;
const EVICT_TO_FRACTION = 0.8;

let sessionReady: Promise<void> | null = null;
let putsSinceCheck = 0;
let evicting = false;

const hasCaches = () => typeof caches !== 'undefined';

function maxBytes(): number {
  const override = (window as any).__helicalDicomCacheMaxBytes;
  return typeof override === 'number' && override > 0 ? override : DEFAULT_MAX_BYTES;
}

async function deleteAll(names: string[]): Promise<void> {
  await Promise.all(
    names.map(async name => {
      try {
        await caches.delete(name);
      } catch {
        /* ignore: a cache that cannot be deleted is still usable */
      }
    })
  );
}

/** Drop the caches earlier builds wrote. Runs once per page. */
export function ensureDicomCacheSession(): Promise<void> {
  if (sessionReady) {
    return sessionReady;
  }

  sessionReady = (async () => {
    if (!hasCaches()) {
      return;
    }
    await deleteAll(LEGACY_CACHE_NAMES);
    try {
      localStorage.removeItem(LEGACY_SESSION_KEY);
    } catch {
      /* ignore */
    }
  })();

  return sessionReady;
}

/** Open the shared cache. */
export async function openDicomCache(): Promise<Cache> {
  await ensureDicomCacheSession();
  return caches.open(CACHE_NAME);
}

/** Drop everything cached now, for example after de-identifying a study. */
export async function clearDicomCache(): Promise<void> {
  if (!hasCaches()) {
    return;
  }
  await deleteAll([CACHE_NAME, ...LEGACY_CACHE_NAMES]);
}

async function usedBytes(): Promise<number | null> {
  try {
    const { usage } = await navigator.storage.estimate();
    return typeof usage === 'number' ? usage : null;
  } catch {
    return null; // Storage API unavailable: eviction is skipped, puts still work
  }
}

/**
 * Delete oldest-first until usage is back under the budget. Usage is reported
 * for the whole origin, which is the figure that matters on a small disk, so
 * this also reacts to IndexedDB and the rest of the origin's storage.
 */
async function evictIfOverBudget(cache: Cache): Promise<void> {
  if (evicting) {
    return;
  }
  evicting = true;
  try {
    const budget = maxBytes();
    let used = await usedBytes();
    if (used === null || used <= budget) {
      return;
    }

    const keys = await cache.keys(); // insertion order: oldest first
    const target = budget * EVICT_TO_FRACTION;

    for (let i = 0; i < keys.length && used !== null && used > target; i += EVICT_BLOCK) {
      const block = keys.slice(i, i + EVICT_BLOCK);
      await Promise.all(block.map(request => cache.delete(request).catch(() => false)));
      used = await usedBytes();
    }
  } finally {
    evicting = false;
  }
}

/**
 * The cache key for a URL. The `vt` parameter is a cache-busting token on
 * signed URLs: the same image arrives under a new token every time, so leaving
 * it in the key stored the image again on every visit.
 */
export function dicomCacheKey(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete('vt');
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * Images served from the configured CDN authenticate with a cookie. Everything
 * else is fetched without credentials, since a browser drops a response whose
 * origin does not allow them.
 */
function fetchInit(url: string): RequestInit {
  const cdn = (window as any).__helicalCloudCdnOrigin;
  return cdn && url.startsWith(cdn) ? { credentials: 'include' } : {};
}

/**
 * Fetch a DICOM object, serving it from the shared cache when it is there.
 *
 * A caching failure is never an error for the caller: the response is returned
 * either way, so a full disk slows the viewer down rather than breaking it.
 */
export async function cachedDicomFetch(url: string, init?: RequestInit): Promise<Response> {
  const requestInit = init ?? fetchInit(url);

  if (!hasCaches()) {
    return fetch(url, requestInit);
  }

  const key = dicomCacheKey(url);

  let cache: Cache;
  try {
    cache = await openDicomCache();
  } catch {
    return fetch(url, requestInit);
  }

  const hit = await cache.match(key).catch(() => undefined);
  if (hit) {
    return hit;
  }

  const response = await fetch(url, requestInit);
  if (!response.ok) {
    return response;
  }

  // not awaited: the disk write can finish after the caller has the bytes
  cache
    .put(key, response.clone())
    .then(() => {
      putsSinceCheck += 1;
      if (putsSinceCheck >= PUTS_BETWEEN_CHECKS) {
        putsSinceCheck = 0;
        void evictIfOverBudget(cache);
      }
    })
    .catch(() => {
      // Out of quota. Evict now so the next put has somewhere to go.
      putsSinceCheck = 0;
      void evictIfOverBudget(cache);
    });

  return response;
}

export const DICOM_CACHE_NAME = CACHE_NAME;
